import axios from "axios";

export const MAIN_APP_URL =
  process.env.REACT_APP_MAIN_APP_URL || "https://call-strategy-hub-1.emergent.host/";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const trackCta = async (ctaId, label, location) => {
  try {
    await axios.post(`${API}/cta-clicks`, {
      cta_id: ctaId,
      label,
      location,
    });
  } catch (e) {
    // tracking is best-effort, never block navigation
  }
};

export const SCREENSHOTS = {
  realLogin: "/shots/login.png",
  analysis: "/shots/analysis.png",
  rollRec: "/shots/roll-rec.png",
  payoff: "/shots/payoff.png",
  tracker: "/shots/tracker-select.png",
  trackerAdd: "/shots/tracker-add.png",
  addForm: "/shots/add-form.png",
};

export const BENEFITS = [
  {
    tag: "01 / Manual & Private",
    title: "Every position is entered by you",
    desc: "ROLL.OPS never connects to your bank or brokerage. You enter your covered calls by hand, so your money, logins and personal financial data stay entirely yours.",
    span: "md:col-span-7",
  },
  {
    tag: "02 / Scenario Modeling",
    title: "Test rolls before you trade",
    desc: "Model different strikes, expirations and premium targets to see how a roll would play out — then decide on your own terms.",
    span: "md:col-span-5",
  },
  {
    tag: "03 / History Log",
    title: "Every move is logged",
    desc: "Each roll is saved to your history as its own entry — a record of what you did, not a running total. Heads up: experiments you make can show up there too.",
    span: "md:col-span-5",
  },
  {
    tag: "04 / Educational by design",
    title: "Learn the mechanics, stay in control",
    desc: "A hands-on learning terminal for income traders. You plan and place every roll yourself, so you understand exactly what you're doing and why.",
    span: "md:col-span-7",
  },
];

export const FEATURES = [
  {
    eyebrow: "Roll Candidates",
    title: "Every roll target, color-coded",
    desc: "Your positions on the left, a full option chain in the middle ranked by net credit and credit %, and a heatmap on the right — all color-coded so the best rolls are obvious at a glance.",
    image: SCREENSHOTS.analysis,
  },
  {
    eyebrow: "Roll Recommendation",
    title: "The full picture before you act",
    desc: "Pick a candidate and get a roll score, net credit, strike-vs-underlying, DTE change and a fresh breakeven — then replace your position or keep both for side-by-side analysis.",
    image: SCREENSHOTS.rollRec,
  },
  {
    eyebrow: "Payoff Comparison",
    title: "See before vs. after at expiration",
    desc: "A P&L payoff chart overlays your current position against the rolled position across the underlying's price range, so you can confirm the roll actually improves your outcome.",
    image: SCREENSHOTS.payoff,
  },
];

export const STEPS = [
  {
    num: "01",
    title: "Open & sign in with Google",
    desc: "Click any 'Open ROLL.OPS' button to launch the terminal, then authorize with one-click Google sign-in. Your private workspace loads instantly — this matches the actual ROLL.OPS sign-in screen.",
    image: SCREENSHOTS.realLogin,
  },
  {
    num: "02",
    title: "Add your positions manually",
    desc: "ROLL.OPS never links to your bank or broker. As an educational tool we deliberately keep your money and login credentials out of reach — you stay in full control. In the Position Tracker, hit '+ Add', pick the ticker, expiration, strike and contracts, then Save Position.",
    images: [
      {
        src: SCREENSHOTS.trackerAdd,
        caption: "1 — Open the Position Tracker and click + Add",
      },
      {
        src: SCREENSHOTS.addForm,
        caption: "2 — Enter ticker, expiration, strike & contracts, then Save",
      },
    ],
  },
  {
    num: "03",
    title: "Click a position to analyze",
    desc: "Once saved, every position appears in the Position Tracker — and it's color-coded at a glance. Click the one you want to work on and ROLL.OPS opens the full roll-analysis view for it.",
    image: SCREENSHOTS.tracker,
    portrait: true,
    colorGuide: [
      {
        title: "Expiration date — time left",
        items: [
          { color: "red", text: "Within 1 week — expiring soon, act now." },
          { color: "amber", text: "Within 2 weeks — start planning your roll." },
          { color: "green", text: "More than 2 weeks out — no rush." },
        ],
      },
      {
        title: "Premium — strike vs. current price",
        items: [
          { color: "green", text: "Strike is above the current price — out of the money, shares safe." },
          { color: "amber", text: "Strike is below the current price — in the money, assignment risk." },
        ],
      },
    ],
  },
  {
    num: "04",
    title: "Read the roll candidates",
    desc: "This is the heart of ROLL.OPS. Every available roll target is laid out and color-coded against two goals: (1) collect a net credit, and (2) move your strike above the underlying price so your shares aren't called away. The heatmap on the right uses the very same colors as the table, so the best zones jump out instantly.",
    image: SCREENSHOTS.analysis,
    wide: true,
    legend: [
      {
        color: "bright",
        label: "Best",
        text: "Everything in 'Ideal', and the premium yields more than 1% per week — the strongest rolls on the board.",
      },
      {
        color: "green",
        label: "Ideal",
        text: "Both goals met — you collect a credit AND the new strike sits above the underlying price, so your shares stay safe from assignment.",
      },
      {
        color: "amber",
        label: "Marginal",
        text: "You still earn a positive premium, but the strike is below the underlying price — your shares could still be called away.",
      },
      {
        color: "red",
        label: "Debit",
        text: "You'd have to pay to make this roll — the net credit is negative.",
      },
    ],
    points: [
      { k: "Real market data", v: "Quotes come live from Tradier — real, live market prices. Analysis uses your real inputs, never fake data." },
      { k: "Position Tracker", v: "Left column — your saved positions. The selected one drives the whole analysis." },
      { k: "Symbol header", v: "Top-center — symbol, live underlying price, the current strike of the selected expiration, and days to expiry." },
      { k: "Expiration tabs", v: "Switch between candidate expirations, or view ALL at once." },
      { k: "Candidates table", v: "Bid/ask, delta, volume, net credit and credit % for every target — color-coded by the rules above." },
      { k: "Heatmap", v: "Right side — a grid of expirations × strikes to spot the green zones at a glance." },
      { k: "Roll Recommendation", v: "Click any candidate to evaluate that specific roll in detail." },
    ],
  },
  {
    num: "05",
    title: "Select your roll & record it",
    desc: "Pick the candidate you want to roll into — click any row in the table, or a cell in the clickable heatmap. The Roll Recommendation panel fills in with every detail. ROLL.OPS never sends the order — you place the actual trade in your own brokerage, then record the outcome here.",
    image: SCREENSHOTS.rollRec,
    wide: true,
    points: [
      { k: "Select a candidate", v: "Click a row in the table or a cell in the heatmap — both jump to the same roll." },
      { k: "Roll Recommendation", v: "Fills with roll score, net credit, credit %, strike vs underlying, DTE change and the new breakeven." },
      { k: "Replace position", v: "Deletes the current position and saves the new one in its place." },
      { k: "Keep + add new", v: "Leaves both in your tracker — great for comparing scenarios side by side." },
      { k: "Saved to history", v: "Either choice records the roll, so you build a running log of what you did." },
      { k: "You execute it", v: "Place the real order in your own brokerage — ROLL.OPS never trades for you." },
    ],
  },
  {
    num: "06",
    title: "Compare before vs. after",
    desc: "Scroll to the P&L · Payoff at Expiration chart to see the whole picture: your current position versus the position after the roll, plotted across the underlying's price range. It's the clearest way to confirm a roll actually improves your outcome before you place it.",
    image: SCREENSHOTS.payoff,
    wide: true,
    points: [
      { k: "After Roll (green)", v: "Your projected payoff if you take this roll — at a glance you see the upside shift." },
      { k: "Current (white)", v: "Your existing position's payoff, for a direct side-by-side comparison." },
      { k: "Per contract", v: "Plotted per contract (×100 shares) using your current underlying as the cost basis." },
      { k: "Full color legend", v: "The chain legend spells out every threshold — bright green for the strongest weekly yields down to red debits." },
      { k: "Logged to history", v: "Replace or keep-and-add saves the roll as an individual entry in your history — a log of each move, experiments included, not a running total." },
    ],
  },
  {
    num: "07",
    title: "Review your rolling history",
    desc: "Once you've been rolling for a while, two history views turn your activity into insight — a clear picture of everything you've done.",
    wideImages: [
      { src: "/shots/roll-history.jpg", caption: "Roll History — every roll and the exact price you rolled at" },
      { src: "/shots/position-timeline.jpg", caption: "Position Timeline — your rolls over time by strike vs. underlying, with each credit shown and totals collected" },
    ],
    points: [
      { k: "Roll History", v: "Every roll you've made and the exact price you rolled at — a perfect visualization of your rolling activity over time." },
      { k: "Position Timeline", v: "A unique view that maps where your rolls sat over time, plotted by strike versus the underlying, with the credit or debit of each roll shown in a comprehensive graphical representation." },
    ],
  },
];

export const FALLBACK_FAQS = [
  {
    id: "faq-what-is",
    question: "What is ROLL.OPS?",
    answer:
      "ROLL.OPS is an educational terminal for planning covered call rolls. Rolling means buying back a call you previously sold and, at the same time, selling a new call against the same shares — usually at a different strike or expiration. For now the tool focuses only on covered calls. You manually enter your positions, model different rolls, and log each move.",
  },
  {
    id: "faq-brokerage",
    question: "Does ROLL.OPS connect to my brokerage or bank?",
    answer:
      "No — and that's intentional. As an educational tool, ROLL.OPS never links to your bank or broker, never sees your credentials, and never touches your money. You enter your positions manually, which keeps your personal financial data fully private and avoids handling regulated account access on your behalf.",
  },
  {
    id: "faq-data",
    question: "Is the data real or simulated?",
    answer:
      "It's real. ROLL.OPS doesn't use fake or sample data — every analysis combines your actual position inputs with live option prices from Tradier. These are real, live market quotes, so you're always working with genuine numbers.",
  },
  {
    id: "faq-execute",
    question: "Does ROLL.OPS place trades for me?",
    answer:
      "No. ROLL.OPS helps you plan, model and compare rolls, but you always execute the actual trade yourself in your own brokerage. You stay in complete control of every order.",
  },
  {
    id: "faq-google",
    question: "How do I sign in?",
    answer:
      "ROLL.OPS uses one-click Google sign-in. Click 'Open ROLL.OPS', authorize with your Google account, and your private workspace loads instantly. Google is used only to identify you — nothing financial is connected.",
  },
  {
    id: "faq-trial",
    question: "What do I get during the trial?",
    answer:
      "All features are fully available during the trial period — nothing is locked or limited. You can add positions, analyze the roll candidates, read the payoff comparison, and log your moves with the complete toolset.",
  },
  {
    id: "faq-secure",
    question: "How secure is my data?",
    answer:
      "Because ROLL.OPS never connects to your bank or broker and you enter positions manually, there are no financial credentials to expose. Sign-in is handled through Google, and your scenarios stay private to your account.",
  },
];
